import React, { useState, useRef, useEffect } from 'react';
import {
  FileIcon,
  MoreVertical,
  EyeIcon,
  EditIcon,
  TrashIcon,
  PlayIcon,
} from 'lucide-react';
import { Asset } from '../pages/AssetsManagement';

interface AssetCardProps {
  asset: Asset;
  viewMode: 'grid' | 'list';
  onPreview: (asset: Asset) => void;
  onDetail: (asset: Asset) => void;
  onDelete: (assetId: string) => void;
  onEditTags: (asset: Asset) => void;
  formatFileSize: (bytes: number) => string;
}

const AssetCard: React.FC<AssetCardProps> = ({
  asset,
  onPreview,
  onDetail,
  onDelete,
  onEditTags,
}) => {
  const [openMenu, setOpenMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getFileTypeBadge = (type: string) => {
    switch (type) {
      case 'image':
        return 'bg-green-100 text-green-700';
      case 'video':
        return 'bg-indigo-100 text-indigo-700';
      case 'pdf':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'image': return 'Image';
      case 'video': return 'Video';
      case 'pdf': return 'PDF';
      default: return 'File';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderThumbnail = () => {
    if (asset.type === 'image' && asset.thumbnailUrl) {
      return (
        <img
          src={asset.thumbnailUrl}
          alt={asset.name}
          className="w-full h-48 object-cover"
        />
      );
    }

    if (asset.type === 'video') {
      if (asset.thumbnailUrl) {
        return (
          <div className="relative w-full h-48">
            <img
              src={asset.thumbnailUrl}
              alt={asset.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-black bg-opacity-60 rounded-full flex items-center justify-center">
                <PlayIcon className="h-6 w-6 text-white ml-0.5" fill="white" />
              </div>
            </div>
          </div>
        );
      }
      return (
        <div className="w-full h-48 bg-gray-800 flex items-center justify-center">
          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <PlayIcon className="h-6 w-6 text-white ml-0.5" fill="white" />
          </div>
        </div>
      );
    }

    if (asset.type === 'pdf') {
      return (
        <div className="w-full h-48 bg-red-50 flex items-center justify-center">
          <span className="text-4xl font-bold text-red-500">PDF</span>
        </div>
      );
    }

    return (
      <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
        <FileIcon className="h-12 w-12 text-gray-400" />
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 hover:shadow transition-shadow flex flex-col group">
      {/* Thumbnail */}
      <div className="relative overflow-hidden cursor-pointer" onClick={() => onPreview(asset)}>
        {renderThumbnail()}
        {/* Hover overlay with eye icon */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="p-3 bg-white bg-opacity-90 rounded-full">
            <EyeIcon className="h-5 w-5 text-gray-700" />
          </div>
        </div>
      </div>

      {/* Info row: name + type badge + menu */}
      <div className="px-4 pt-3 pb-2 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{asset.name}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${getFileTypeBadge(asset.type)}`}>
              {getTypeLabel(asset.type)}
            </span>
          </div>
          {/* Three-dot menu */}
          <div className="relative flex-shrink-0 ml-2" ref={menuRef}>
            <button
              onClick={() => setOpenMenu(!openMenu)}
              className="p-1 text-gray-700 hover:text-gray-800"
            >
              <MoreVertical className="h-5 w-5" />
            </button>
            {openMenu && (
              <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 shadow-lg z-10 rounded">
                <button
                  onClick={() => { onDetail(asset); setOpenMenu(false); }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <EyeIcon className="h-4 w-4 mr-2" />
                  Preview
                </button>
                <button
                  onClick={() => { onEditTags(asset); setOpenMenu(false); }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <EditIcon className="h-4 w-4 mr-2" />
                  Edit Tags
                </button>
                <button
                  onClick={() => { onDelete(asset.id); setOpenMenu(false); }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-1 justify-between h-24'>
        {/* Tags */}
        <div className="px-4 pb-2">
          {asset.tags && asset.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {asset.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">No tag assigned</p>
          )}
        </div>

        {/* Date */}
        <div className="px-4 py-3">
          <p className="text-xs text-gray-500">{formatDate(asset.uploadDate)}</p>
        </div>
      </div>
    </div>
  );
};

export default AssetCard;
